import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const BUMSCoursePage: React.FC = () => {
  const courseData = {
    title: "BUMS - Bachelor of Unani Medicine & Surgery",
    shortName: "BUMS",
    duration: "5.5 years (4.5 years + 1 year internship)",
    description: "Traditional Arabic-Persian medicine system focusing on natural healing and holistic treatment",
    overview: "Bachelor of Unani Medicine and Surgery (BUMS) is an undergraduate medical degree that focuses on the Unani system of medicine - a traditional healing system developed by Arabic and Persian physicians. This course trains students to diagnose and treat diseases using natural remedies, dietary modifications, and therapeutic procedures based on the principles of balancing the body's humors.",
    eligibility: [
      "10+2 with Physics, Chemistry, Biology",
      "Minimum 50% marks (40% for SC/ST)",
      "NEET qualification mandatory",
      "Age: 17-25 years"
    ],
    
    curriculum: [
      {
        year: 1,
        subjects: [
          "Kulliyat (Basic Principles of Unani Medicine)",
          "Tashreeh-ul-Badan (Anatomy)",
          "Manteq (Logic)",
          "Lisan-ul-Quran (Arabic)",
          "Tareekh-e-Tib (History of Medicine)",
          "Basic Sciences"
        ]
      },
      {
        year: 2,
        subjects: [
          "Tashreeh-ul-Badan (Advanced Anatomy)",
          "Afaal-e-Azaa (Physiology)",
          "Tahaffuzi-wa-Samaji-Tib (Preventive Medicine)",
          "Ilmul-Advia (Pharmacology)",
          "Saidla (Pharmacy)",
          "Research Methodology"
        ]
      },
      {
        year: 3,
        subjects: [
          "Amraz (Pathology)",
          "Mahiyat-ul-Amraz (General Pathology)",
          "Munafeul-Azaa (Applied Physiology)",
          "Ilaj-Bit-Tadbeer (Regimenal Therapy)",
          "Jarahat (Surgery)",
          "Qanoon-e-Tib (Canon of Medicine)"
        ]
      },
      {
        year: 4,
        subjects: [
          "Moalajat (Medicine)",
          "Jarahat (Surgery - Clinical)",
          "Ilm-e-Qabalat-wa-Niswan (Obstetrics & Gynecology)",
          "Ilm-ul-Atfal (Pediatrics)",
          "Ain-wa-Uzun-wa-Halaq-wa-Anf (ENT & Ophthalmology)",
          "Clinical Training"
        ]
      },
      {
        year: 5,
        subjects: [
          "Clinical Training in Unani Hospitals",
          "Practical Medicine Application",
          "Patient Case Management",
          "Research Project",
          "Community Health Programs",
          "Internship Rotations"
        ]
      }
    ],

    careerProspects: {
      roles: [
        "Unani Medical Officer",
        "Unani Physician",
        "Research Officer",
        "Drug Inspector",
        "Medical Consultant",
        "Professor/Lecturer",
        "Hospital Administrator",
        "Pharmaceutical Analyst",
        "Health Writer",
        "Private Practitioner"
      ],
      industries: [
        "Government Hospitals",
        "AYUSH Departments",
        "Private Unani Clinics",
        "Research Institutions",
        "Educational Institutions",
        "Pharmaceutical Companies",
        "Drug Regulatory Bodies",
        "NGOs",
        "Wellness Centers",
        "Health Tourism"
      ],
      averageSalary: "₹2.5-6 LPA",
      topRecruiters: [
        "Central Council of Indian Medicine",
        "State Health Departments",
        "Hamdard Laboratories",
        "Unani Medical Colleges",
        "Research Organizations",
        "Private Healthcare"
      ]
    },

    topColleges: [
      {
        name: "Ajmal Khan Tibbiya College",
        location: "Aligarh, Uttar Pradesh",
        fees: "₹20,000/year",
        ranking: "9.1/10"
      },
      {
        name: "Hamdard University",
        location: "New Delhi",
        fees: "₹65,000/year",
        ranking: "8.9/10"
      },
      {
        name: "Government Nizamia Tibbi College",
        location: "Hyderabad, Telangana",
        fees: "₹15,000/year",
        ranking: "8.7/10"
      },
      {
        name: "Takmeel-ut-Tib College",
        location: "Lucknow, Uttar Pradesh",
        fees: "₹25,000/year",
        ranking: "8.5/10"
      },
      {
        name: "Z.V.M. Unani Medical College",
        location: "Pune, Maharashtra",
        fees: "₹45,000/year",
        ranking: "8.3/10"
      }
    ],

    skills: [
      "Traditional Diagnostic Methods",
      "Pulse Diagnosis (Nabz)",
      "Unani Pharmacology",
      "Regimenal Therapy",
      "Cupping (Hijama)",
      "Dietary Counseling",
      "Arabic Language",
      "Classical Unani Texts",
      "Natural Medicine Preparation",
      "Holistic Patient Care"
    ],

    futureScope: "Growing global interest in traditional medicine, government support for AYUSH systems, integration with modern healthcare, research opportunities in traditional healing, international recognition of Unani medicine, wellness industry expansion, pharmaceutical research applications, academic career opportunities, and establishment of specialized Unani treatment centers.",

    faqs: [
      {
        question: "Is BUMS a recognized medical degree?",
        answer: "Yes, BUMS is a recognized medical degree under the Central Council of Indian Medicine (CCIM) and Ministry of AYUSH, Government of India."
      },
      {
        question: "What is the difference between BUMS and other AYUSH degrees?",
        answer: "BUMS focuses on Unani medicine (Arabic-Persian system), while BAMS focuses on Ayurveda and BHMS on Homeopathy. Each has different diagnostic and treatment approaches."
      },
      {
        question: "Can BUMS graduates work in government hospitals?",
        answer: "Yes, BUMS graduates can work as Medical Officers in government hospitals, AYUSH departments, and community health centers."
      },
      {
        question: "Is NEET required for BUMS admission?",
        answer: "Yes, NEET is mandatory for admission to BUMS courses in all recognized Unani medical colleges across India."
      },
      {
        question: "What specializations are available after BUMS?",
        answer: "You can pursue MD (Unani) in specializations like Moalajat (Medicine), Jarahat (Surgery), Ilm-ul-Qabalat (Gynecology), and others."
      },
      {
        question: "What is the scope of Unani medicine internationally?",
        answer: "Unani medicine is recognized in several countries including Middle Eastern nations, and there's growing international interest in traditional healing systems."
      }
    ],

    admissionProcess: [
      "NEET Qualification",
      "State/National Counseling Registration",
      "Choice Filling & Document Verification",
      "Seat Allotment",
      "College Reporting & Fee Payment"
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0a183d] via-black to-[#1a2746] flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow animate-glow mb-8 text-center">
        {courseData.title}
      </h1>
      <div className="w-full max-w-5xl">
        <CourseTemplate courseData={courseData} />
      </div>
    </div>
  );
};

export default BUMSCoursePage;
